import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DataContext } from '../context/DataContext';
import PinnedTransactionsDocument from './PinnedTransactionsDocument';

const Documents = () => {
  const { caseInfo } = useContext(DataContext);
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [openPinnedDialog, setOpenPinnedDialog] = useState(false);
  const [openResearchNoteDialog, setOpenResearchNoteDialog] = useState(false);
  const [openNewDocumentDialog, setOpenNewDocumentDialog] = useState(false);
  const [newDocumentCaseId, setNewDocumentCaseId] = useState('');
  const [newDocumentContent, setNewDocumentContent] = useState('');

  useEffect(() => {
    const savedDocuments = JSON.parse(localStorage.getItem('documents') || '[]');
    setDocuments(savedDocuments);
  }, []);

  const handleOpenDocument = (document) => {
    setSelectedDocument(document);
    if (document.type === 'pinned_transactions') {
      setOpenPinnedDialog(true);
    } else if (document.type === 'research_note') {
      setOpenResearchNoteDialog(true);
    }
  };

  const handleOpenNewDocumentDialog = () => {
    setOpenNewDocumentDialog(true);
  };

  const handleCloseNewDocumentDialog = () => {
    setOpenNewDocumentDialog(false);
    setNewDocumentCaseId('');
    setNewDocumentContent('');
  };

  const handleSaveNewDocument = () => {
    if (!newDocumentCaseId || !newDocumentContent) {
      alert('Please select a case number and enter document content.');
      return;
    }

    const newDocument = {
      id: `${Date.now()}`,
      case_id: newDocumentCaseId,
      name: `Document-${Date.now()}`,
      content: newDocumentContent,
      created_at: new Date().toISOString(),
      created_by: 'User',
      type: 'research_note',
    };

    const updatedDocuments = [...documents, newDocument];
    setDocuments(updatedDocuments);
    localStorage.setItem('documents', JSON.stringify(updatedDocuments));
    handleCloseNewDocumentDialog();
  };

  const handleDeleteDocument = (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      const updatedDocuments = documents.filter((doc) => doc.id !== documentId);
      setDocuments(updatedDocuments);
      localStorage.setItem('documents', JSON.stringify(updatedDocuments));
    }
  };

  const columns = [
    { field: 'case_id', headerName: 'Case Number', flex: 1, align: 'center',
      headerAlign: 'center' },
    { field: 'name', headerName: 'Document Name', flex: 1, align: 'center',
      headerAlign: 'center' },
    {
      field: 'created_at',
      headerName: 'Created Time',
      align: 'center',
      headerAlign: 'center',
      flex: 1,
      valueFormatter: ({ value }) => {
        const date = new Date(value);
        return '8 May 2025'
      },
    },
    { field: 'created_by', headerName: 'Created By', flex: 1, align: 'center',
      headerAlign: 'center'},
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 4,
          width: '100%',
          height: '100%',}}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleOpenDocument(params.row)}
          >
            Open
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => handleDeleteDocument(params.row.id)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  const caseList = Array.isArray(caseInfo) ? caseInfo : [];

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Documents
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenNewDocumentDialog}
        >
          New Document
        </Button>
      </Box>
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <DataGrid
          autoHeight
          rows={documents}
          columns={columns}
          getRowId={(row) => row.id}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          sx={{ width: '100%' }}
        />
      </Box>
      <PinnedTransactionsDocument
        open={openPinnedDialog}
        onClose={() => setOpenPinnedDialog(false)}
        caseId={selectedDocument?.case_id}
      />
      <Dialog
        open={openResearchNoteDialog}
        onClose={() => setOpenResearchNoteDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Research Note: {selectedDocument?.name}</DialogTitle>
        <DialogContent>
          <Typography>{selectedDocument?.content || 'No content available'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResearchNoteDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openNewDocumentDialog}
        onClose={handleCloseNewDocumentDialog}
        sx={{ '& .MuiDialog-paper': { width: '600px', height: '400px' } }}
      >
        <DialogTitle>Create New Document</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="case-id-label">Case Number</InputLabel>
            <Select
              labelId="case-id-label"
              value={newDocumentCaseId}
              label="Case Number"
              onChange={(e) => setNewDocumentCaseId(e.target.value)}
            >
              {caseList.length > 0 ? (
                caseList.map((caseItem) => (
                  <MenuItem key={caseItem.case_number} value={caseItem.case_number}>
                    REF-{caseItem.case_number}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No cases available</MenuItem>
              )}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Document Content"
            value={newDocumentContent}
            onChange={(e) => setNewDocumentContent(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNewDocumentDialog}>Cancel</Button>
          <Button onClick={handleSaveNewDocument} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Documents;